import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Sun, Zap, DollarSign, TrendingDown, Calendar, ArrowRight } from 'lucide-react';
import { WHATSAPP_LINK } from '../data/mock';
import SEO from '../components/SEO';

const Calculator = () => {
  const [monthlyBill, setMonthlyBill] = useState(500);
  const [energyPrice, setEnergyPrice] = useState(0.85);
  
  // Calculations
  const monthlyConsumption = Math.round(monthlyBill / energyPrice);
  const systemSize = Math.round((monthlyConsumption * 12) / 1350 * 10) / 10; // kWp
  const panelCount = Math.ceil(systemSize / 0.55); // 550W panels
  const estimatedCost = Math.round(systemSize * 4500);
  const monthlySavings = Math.round(monthlyBill * 0.95);
  const yearlySavings = monthlySavings * 12;
  const paybackYears = Math.round((estimatedCost / yearlySavings) * 10) / 10;
  const savings25Years = yearlySavings * 25;

  const handleWhatsAppQuote = () => {
    const message = encodeURIComponent(
      `Olá! Fiz uma simulação na calculadora solar e gostaria de um orçamento:\n\n` +
      `💡 Conta mensal: R$ ${monthlyBill}\n` +
      `⚡ Consumo mensal: ${monthlyConsumption} kWh\n` +
      `🔆 Sistema necessário: ${systemSize} kWp\n` +
      `📊 Investimento estimado: R$ ${estimatedCost.toLocaleString()}\n\n` +
      `Gostaria de mais informações!`
    );
    window.open(`${WHATSAPP_LINK}?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen pt-16">
      <SEO 
        title="Calculadora Solar - W3Solar | Simule sua Economia com Energia Solar"
        description="Calcule quanto você pode economizar com energia solar. Simule o investimento, retorno e economia em 25 anos. Orçamento grátis!"
        keywords="calculadora solar, simulador energia solar, calcular economia solar, quanto custa painel solar"
      />
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 bg-gradient-to-br from-emerald-50 via-teal-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full mb-6">
              <Sun className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Calculadora Solar
            </h1>
            <p className="text-xl text-gray-600">
              Descubra quanto você pode economizar com energia solar e o retorno do seu investimento
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Input Section */}
            <div>
              <Card className="border-2 border-emerald-200">
                <CardHeader>
                  <CardTitle className="text-2xl">Dados para Simulação</CardTitle>
                  <CardDescription>
                    Informe os dados da sua conta de luz para calcular a economia
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-8">
                  {/* Monthly Bill */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Label htmlFor="monthly-bill" className="text-base font-semibold">
                        Valor da conta mensal
                      </Label>
                      <div className="flex items-center space-x-2">
                        <DollarSign className="h-5 w-5 text-emerald-600" />
                        <span className="text-2xl font-bold text-emerald-700">
                          R$ {monthlyBill}
                        </span>
                      </div>
                    </div>
                    <Slider
                      id="monthly-bill"
                      min={100}
                      max={5000}
                      step={50}
                      value={[monthlyBill]}
                      onValueChange={(value) => setMonthlyBill(value[0])}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>R$ 100</span>
                      <span>R$ 5.000</span>
                    </div>
                  </div>

                  {/* Energy Price */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Label htmlFor="energy-price" className="text-base font-semibold">
                        Preço do kWh
                      </Label>
                      <div className="flex items-center space-x-2">
                        <Zap className="h-5 w-5 text-emerald-600" />
                        <span className="text-2xl font-bold text-emerald-700">
                          R$ {energyPrice.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <Slider
                      id="energy-price"
                      min={0.5}
                      max={1.5}
                      step={0.05}
                      value={[energyPrice]}
                      onValueChange={(value) => setEnergyPrice(value[0])}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>R$ 0,50</span>
                      <span>R$ 1,50</span>
                    </div>
                  </div>

                  {/* Consumption Info */}
                  <div className="bg-emerald-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-2">Seu consumo mensal estimado:</p>
                    <p className="text-3xl font-bold text-emerald-700">{monthlyConsumption} kWh</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Results Section */}
            <div className="space-y-6">
              <Card className="border-2 border-emerald-400 shadow-lg">
                <CardHeader className="bg-gradient-to-br from-emerald-50 to-teal-50">
                  <CardTitle className="text-2xl">Resultados da Simulação</CardTitle>
                  <CardDescription>
                    Estimativa baseada nos seus dados
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                  {/* System Size */}
                  <div className="flex justify-between items-center pb-4 border-b">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Sistema Necessário</p>
                      <p className="text-3xl font-bold text-gray-900">{systemSize} kWp</p>
                    </div>
                    <Sun className="h-12 w-12 text-emerald-600" />
                  </div>

                  {/* Panel Count */}
                  <div className="flex justify-between items-center pb-4 border-b">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Painéis Solares</p>
                      <p className="text-3xl font-bold text-gray-900">{panelCount} unidades</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Painéis de 550W</p>
                    </div>
                  </div>

                  {/* Investment */}
                  <div className="flex justify-between items-center pb-4 border-b">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Investimento Estimado</p>
                      <p className="text-3xl font-bold text-emerald-700">
                        R$ {estimatedCost.toLocaleString()}
                      </p>
                    </div>
                    <DollarSign className="h-12 w-12 text-emerald-600" />
                  </div>

                  {/* Monthly Savings */}
                  <div className="flex justify-between items-center pb-4 border-b">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Economia Mensal</p>
                      <p className="text-3xl font-bold text-emerald-700">
                        R$ {monthlySavings}
                      </p>
                    </div>
                    <TrendingDown className="h-12 w-12 text-emerald-600" />
                  </div>

                  {/* Payback */}
                  <div className="flex justify-between items-center pb-4 border-b">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Retorno do Investimento</p>
                      <p className="text-3xl font-bold text-gray-900">{paybackYears} anos</p>
                    </div>
                    <Calendar className="h-12 w-12 text-emerald-600" />
                  </div>

                  {/* 25 Year Savings */}
                  <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg p-6 text-white">
                    <p className="text-sm mb-2 text-emerald-100">Economia Total em 25 anos</p>
                    <p className="text-4xl font-bold mb-1">
                      R$ {savings25Years.toLocaleString()}
                    </p>
                    <p className="text-sm text-emerald-100">
                      Considerando a vida útil dos painéis
                    </p>
                  </div>

                  {/* CTA */}
                  <Button
                    onClick={handleWhatsAppQuote}
                    size="lg"
                    className="w-full bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 text-white text-lg py-6"
                  >
                    Solicitar Orçamento Detalhado
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </CardContent>
              </Card>

              {/* Disclaimer */}
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="pt-6">
                  <p className="text-sm text-amber-900">
                    <strong>Importante:</strong> Esta é uma simulação estimada. Os valores reais podem variar de acordo com o tipo de telhado, localização, incidência solar e outros fatores. Entre em contato para um orçamento personalizado e preciso.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Por que investir agora?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card>
              <CardHeader>
                <CardTitle>Valorização Imobiliária</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Imóveis com sistema solar valorizam até 30% no mercado, além de serem mais atrativos para locação e venda.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Proteção contra Inflação</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Energia elétrica aumenta em média 10% ao ano. Com energia solar, você se protege desses aumentos.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Sustentabilidade</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Cada sistema evita a emissão de toneladas de CO₂ na atmosfera, contribuindo para um planeta mais limpo.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Calculator;
